import { z } from 'zod';
import {
  linkSchema,
  mediaFileSchema,
  skillCategorySchema,
  strapiEntitySchema,
} from './common.js';

// Base schemas hold everything except entity-to-entity relations. Relation
// fields live in the per-entity full schemas and point back at these bases,
// which keeps the module graph acyclic (populate=* only goes one level deep,
// so nested relation items never carry relations of their own anyway).
// `categories` is nullish here and tightened to required on the full schemas:
// components are not populated on nested relation items.

export const strapiSkillBaseSchema = strapiEntitySchema.extend({
  name: z.string(),
  slug: z.string(),
  description: z.string().nullish(),
  categories: z.array(skillCategorySchema).nullish(),
  // json CMS field — lenient: malformed content degrades to null, never rejects.
  tags: z.array(z.string()).nullish().catch(null),
  proficiencyLevel: z
    .enum(['beginner', 'intermediate', 'advanced', 'expert'])
    .nullish(),
  yearsOfExperience: z.number().nullish(),
  icon: z.string().nullish(),
  link: z.string().nullish(),
  isActive: z.boolean(),
  isFeatured: z.boolean(),
});

export type StrapiSkillBase = z.infer<typeof strapiSkillBaseSchema>;

export const strapiEducationBaseSchema = strapiEntitySchema.extend({
  institution: z.string(),
  // Optional in the CMS schema; the mapper falls back to String(id).
  slug: z.string().nullish(),
  degree: z.string(),
  fieldOfStudy: z.string(),
  level: z.enum([
    'high-school',
    'diploma',
    'associate',
    'bachelor',
    'master',
    'doctorate',
    'certification',
    'course',
    'bootcamp',
    'workshop',
    'other',
  ]),
  startDate: z.string(),
  endDate: z.string().nullish(),
  location: z.string().nullish(),
  gpa: z.number().nullish(),
  description: z.string().nullish(),
  summary: z.string().nullish(),
  achievements: z.unknown(),
  logo: mediaFileSchema.nullish(),
  diploma: mediaFileSchema.nullish(),
  transcript: mediaFileSchema.nullish(),
  links: z.array(linkSchema).nullish(),
  isFeatured: z.boolean(),
});

export type StrapiEducationBase = z.infer<typeof strapiEducationBaseSchema>;

export const strapiAwardBaseSchema = strapiEntitySchema.extend({
  title: z.string(),
  slug: z.string(),
  issuer: z.string(),
  type: z.enum([
    'hackathon-winner',
    'competition-winner',
    'conference-speaker',
    'community-contribution',
    'open-source-contribution',
    'research-paper',
    'patent',
    'certification-achievement',
    'academic-achievement',
    'technical-excellence',
    'innovation-award',
    'industry-recognition',
    'other',
  ]),
  categories: z.array(skillCategorySchema).nullish(),
  date: z.string(),
  description: z.string().nullish(),
  summary: z.string().nullish(),
  criteria: z.string().nullish(),
  impact: z.string().nullish(),
  logo: mediaFileSchema.nullish(),
  certificate: mediaFileSchema.nullish(),
  links: z.array(linkSchema).nullish(),
  isFeatured: z.boolean(),
});

export type StrapiAwardBase = z.infer<typeof strapiAwardBaseSchema>;

export const strapiCertificationBaseSchema = strapiEntitySchema.extend({
  name: z.string(),
  slug: z.string(),
  issuer: z.string(),
  categories: z.array(skillCategorySchema).nullish(),
  level: z
    .enum([
      'foundational',
      'associate',
      'professional',
      'specialty',
      'expert',
      'master',
      'other',
    ])
    .nullish(),
  issueDate: z.string(),
  expiryDate: z.string().nullish(),
  credentialId: z.string().nullish(),
  description: z.string().nullish(),
  summary: z.string().nullish(),
  logo: mediaFileSchema.nullish(),
  certificate: mediaFileSchema.nullish(),
  badge: mediaFileSchema.nullish(),
  links: z.array(linkSchema).nullish(),
  isFeatured: z.boolean(),
});

export type StrapiCertificationBase = z.infer<
  typeof strapiCertificationBaseSchema
>;

export const strapiProjectBaseSchema = strapiEntitySchema.extend({
  title: z.string(),
  slug: z.string(),
  description: z.string(),
  summary: z.string().nullish(),
  type: z.enum([
    'work',
    'personal',
    'open-source',
    'freelance',
    'academic',
    'hackathon',
    'research',
  ]),
  currentStatus: z.enum([
    'planning',
    'in-progress',
    'completed',
    'on-hold',
    'cancelled',
    'maintenance',
  ]),
  startDate: z.string().nullish(),
  endDate: z.string().nullish(),
  coverImage: mediaFileSchema.nullish(),
  gallery: z.array(mediaFileSchema).nullish(),
  links: z.array(linkSchema).nullish(),
  achievements: z.string().nullish(),
  challenges: z.string().nullish(),
  learnings: z.string().nullish(),
  role: z.string().nullish(),
  teamSize: z.number().nullish(),
  client: z.string().nullish(),
  isFeatured: z.boolean(),
  isPublic: z.boolean(),
});

export type StrapiProjectBase = z.infer<typeof strapiProjectBaseSchema>;

export const strapiExperienceBaseSchema = strapiEntitySchema.extend({
  title: z.string(),
  slug: z.string(),
  company: z.string(),
  type: z.enum([
    'full-time',
    'part-time',
    'contract',
    'freelance',
    'consulting',
    'internship',
    'volunteer',
    'other',
  ]),
  position: z.string(),
  startDate: z.string(),
  endDate: z.string().nullish(),
  industry: z
    .enum([
      'technology',
      'banking-finance',
      'aviation',
      'iot-manufacturing',
      'healthcare',
      'education',
      'ecommerce',
      'entertainment',
      'government',
      'non-profit',
      'consulting',
      'real-estate',
      'transportation',
      'energy',
      'telecommunications',
      'other',
    ])
    .nullish(),
  location: z.string().nullish(),
  workMode: z.enum(['on-site', 'remote', 'hybrid']).nullish(),
  description: z.string(),
  summary: z.string().nullish(),
  // json CMS field — lenient: malformed content degrades to null, never rejects.
  achievements: z.array(z.string()).nullish().catch(null),
  categories: z.array(skillCategorySchema).nullish(),
  logo: mediaFileSchema.nullish(),
  links: z.array(linkSchema).nullish(),
  isFeatured: z.boolean(),
});

export type StrapiExperienceBase = z.infer<typeof strapiExperienceBaseSchema>;
